export type TeamColor = { stroke: string; fill: string };

export type TeamMember = {
  name: string;
  email: string;
  initials: string;
  color: TeamColor;
};

const PALETTE: TeamColor[] = [
  { stroke: "#4eaad9", fill: "#1d5f80" },
  { stroke: "#e8873a", fill: "#af6532" },
  { stroke: "#7fd99a", fill: "#2f6b45" },
  { stroke: "#c792ea", fill: "#5a3d7a" },
  { stroke: "#e8c36a", fill: "#8a6a24" },
  { stroke: "#f07178", fill: "#8a3038" },
  { stroke: "#4ecdc4", fill: "#1d6b66" },
  { stroke: "#9bb0ff", fill: "#3d4f9a" },
];

export const GPS_OFF_COLOR: TeamColor = { stroke: "#8b9298", fill: "#4d5863" };

/** Show-floor roster: first + last initials, assigned tile/GPS color. */
export const IRONFLEET_TEAM: readonly TeamMember[] = [
  { name: "John Gale", email: "john.gale@ironfleet.ai", initials: "JG", color: PALETTE[0] },
  { name: "Lucy Nicol-Lints", email: "lucy@ironfleet.ai", initials: "LN", color: PALETTE[2] },
  { name: "Jason Lefton", email: "jason@ironfleet.ai", initials: "JL", color: PALETTE[3] },
  { name: "Mike Brown", email: "mike.brown@ironfleet.ai", initials: "MB", color: PALETTE[4] },
  { name: "Christian Malcolm", email: "christian@ironfleet.ai", initials: "CM", color: PALETTE[5] },
  { name: "Melissa Hale", email: "melissa@ironfleet.ai", initials: "MH", color: PALETTE[6] },
  { name: "Kevin DeCoster", email: "kevin@ironfleet.ai", initials: "KD", color: PALETTE[1] },
];

export function teamInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

function norm(value: string): string {
  return value.trim().toLowerCase();
}

export function findTeamMember(key: string): TeamMember | undefined {
  const needle = norm(key);
  if (!needle) return undefined;
  return IRONFLEET_TEAM.find(
    (member) =>
      norm(member.email) === needle ||
      norm(member.name) === needle ||
      norm(member.initials) === needle,
  );
}

export function teamMemberFromPresence(entry: {
  email?: string | null;
  display_name?: string | null;
  user_id?: string | null;
}): TeamMember | undefined {
  return (
    findTeamMember(entry.email ?? "") ||
    findTeamMember(entry.display_name ?? "") ||
    findTeamMember(entry.user_id ?? "")
  );
}

export function presenceHasGps(entry?: { gps_on?: boolean; gps_xy?: [number, number] | null } | null): boolean {
  if (!entry) return false;
  if (entry.gps_on === false) return false;
  if (entry.gps_on === true) return true;
  return Boolean(entry.gps_xy);
}

export function memberPresence(
  member: TeamMember,
  presence: Array<{
    email?: string | null;
    display_name?: string | null;
    user_id?: string | null;
    gps_on?: boolean;
    gps_xy?: [number, number] | null;
    online?: boolean;
  }>,
) {
  return presence.find((row) => teamMemberFromPresence(row)?.email === member.email);
}

export function memberHasGps(
  member: TeamMember,
  presence: Array<{
    email?: string | null;
    display_name?: string | null;
    user_id?: string | null;
    gps_on?: boolean;
    gps_xy?: [number, number] | null;
  }>,
  opts?: { selfEmail?: string; selfUserId?: string; selfGpsOn?: boolean },
): boolean {
  const selfEmail = norm(opts?.selfEmail ?? "");
  const selfRow = opts?.selfUserId
    ? presence.find((row) => row.user_id === opts.selfUserId)
    : undefined;
  const isSelf =
    Boolean(selfEmail && norm(member.email) === selfEmail) ||
    Boolean(selfRow && teamMemberFromPresence(selfRow)?.email === member.email);
  if (isSelf && opts?.selfGpsOn !== undefined) return opts.selfGpsOn;
  const entry = memberPresence(member, presence);
  return presenceHasGps(isSelf ? selfRow ?? entry : entry);
}

export function memberIsOnline(
  member: TeamMember,
  presence: Array<{
    email?: string | null;
    display_name?: string | null;
    user_id?: string | null;
    online?: boolean;
  }>,
  opts?: { selfEmail?: string; selfUserId?: string; selfOnline?: boolean },
): boolean {
  const selfEmail = norm(opts?.selfEmail ?? "");
  const selfRow = opts?.selfUserId
    ? presence.find((row) => row.user_id === opts.selfUserId)
    : undefined;
  const isSelf =
    Boolean(selfEmail && norm(member.email) === selfEmail) ||
    Boolean(selfRow && teamMemberFromPresence(selfRow)?.email === member.email);
  if (isSelf && opts?.selfOnline !== undefined) return opts.selfOnline;
  return Boolean(memberPresence(member, presence)?.online);
}

export function selfPresence<T extends { user_id?: string | null; email?: string | null }>(
  presence: T[],
  opts: { userId?: string; email?: string },
): T | undefined {
  const email = norm(opts.email ?? "");
  const userId = (opts.userId ?? "").trim();
  return presence.find(
    (entry) =>
      Boolean(userId && entry.user_id === userId) || Boolean(email && norm(entry.email ?? "") === email),
  );
}

export function tileColor(member: TeamMember, gpsOn: boolean): TeamColor {
  return gpsOn ? member.color : GPS_OFF_COLOR;
}

export function teamColor(userId: string): TeamColor {
  const named = findTeamMember(userId);
  if (named) return named.color;
  let hash = 0;
  const key = userId.trim() || "team";
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return PALETTE[hash % PALETTE.length];
}

export function teamPalette(): readonly TeamColor[] {
  return PALETTE;
}
